/* eslint-disable @typescript-eslint/no-explicit-any */
import ReactGA from "react-ga4";
import ReactPixel, { AdvancedMatching } from "react-facebook-pixel";
import TagManager from "react-gtm-module";

import posthog, { Properties } from "posthog-js";

declare global {
  interface Window {
    posthog: typeof posthog;
  }
}

import { getReactAppConfig } from "shared/functions";
import { FbPixelId } from "shared/constants/fb-pixel-id";

type Analytics = {
  initializeAll: () => void; // Initialize all analytics services
  initializeGA: () => void; // Google Analytics
  initializeFPixel: () => void; // Facebook Pixel
  initializePostHog: () => void; // PostHog
  initializeGTM: () => void; // Google Tag Manager

  // Posthog methods
  sendPosthogEvent: (event: string, properties?: Properties) => void;
  sendPageleavePosthog: (path: string) => void;

  // Google Analytics methods
  sendEvent: (event: string, properties?: Properties) => void;

  // Meta (Facebook) Pixel methods
  sendPixelEvent: (
    event: string,
    properties?: Properties,
    custom?: boolean
  ) => void;

  // Combined methods
  setUserId: (userId: string) => void;
  setUserInfo: (
    userId: string,
    properties?: {
      email?: string;
      activeTrial?: boolean;
      activePaidSubscriptionName?: string;
    }
  ) => void;
  removeUserId: () => void;

  sendPageview: (path: string) => void;
  sendConversionEvent: (
    event: string,
    properties?: Properties | null,
    custom?: boolean // Custom event for Facebook Pixel
  ) => void;
};

const analyticsService = (): Analytics => {
  const config = getReactAppConfig();

  // Google Analytics
  const initializeGA = () => ReactGA.initialize(config.REACT_APP_GA_ID);

  const removeUserId = () => {
    posthog.reset();
    ReactGA.set({ userId: null });
    ReactPixel.init(FbPixelId, {} as AdvancedMatching);
  };

  const setUserId = (userId: string) => {
    posthog.identify(userId);
    posthog.reloadFeatureFlags();

    ReactGA.set({ userId });
  };

  const setUserInfo = (
    userId: string,
    properties?: {
      email?: string;
      activeTrial?: boolean;
      activePaidSubscriptionName?: string;
    }
  ) => {
    posthog.identify(userId, properties);
    posthog.reloadFeatureFlags();

    ReactGA.set({
      userId,
      activeTrial: properties?.activeTrial,
      activePaidSubscriptionName: properties?.activePaidSubscriptionName,
    });

    if (properties?.email) {
      ReactPixel.init(FbPixelId, {
        external_id: userId,
        em: properties.email,
      } as any);
    }
  };

  const sendEvent = (event: string) => ReactGA.event(event);

  const sendPageview = (path: string) => {
    ReactGA.send({
      hitType: "pageview",
      page: path,
    });
    posthog.capture("$pageview");
  };

  const sendPageleavePosthog = (path: string) => {
    posthog.capture("$pageleave", { pathname: path });
  };

  const sendPosthogEvent = (event: string, properties?: Properties) => {
    posthog.capture(event, properties);
  };

  const sendPixelEvent = (
    event: string,
    properties?: Properties,
    custom = false
  ) => {
    if (custom) {
      ReactPixel.trackCustom(event, properties);
    } else {
      ReactPixel.track(event, properties);
    }
  };

  // Facebook Pixel
  const initializeFPixel = () => {
    ReactPixel.init(FbPixelId);
    ReactPixel.pageView();
  };

  // PostHog
  const initializePostHog = () => {
    posthog.init(config.REACT_APP_POSTHOG_API_KEY, {
      api_host: config.REACT_APP_POSTHOG_HOST,
      capture_pageview: false, // track manually
      capture_pageleave: true, // navigation to other websites/refresh/close tab
      session_recording: {
        maskTextSelector: '[data-posthog-mask="true"]',
        maskAllInputs: false,
        maskInputOptions: {
          password: true,
        },
      },
    });

    window.posthog = posthog;
  };

  // Google Tag Manager
  const initializeGTM = () => {
    const tagManagerArgs = {
      gtmId: config.REACT_APP_GTM_CONTAINER_ID,
      auth: config.REACT_APP_GTM_ENV_AUTH,
      preview: config.REACT_APP_GTM_ENV_PREVIEW,
    };

    TagManager.initialize(tagManagerArgs);
  };

  const initializeAll = () => {
    initializeGTM();
    initializeGA();
    initializeFPixel();
    initializePostHog();
  };

  const sendConversionEvent = (
    event: string,
    properties?: Properties | null,
    custom?: boolean
  ) => {
    if (custom) {
      ReactPixel.trackCustom(event, properties);
    } else {
      ReactPixel.track(event, properties);
    }
    ReactGA.event(event, properties);
    posthog.capture(event, properties);
  };

  return {
    initializeAll,
    initializeGA,
    initializeFPixel,
    initializePostHog,
    initializeGTM,

    setUserId,
    setUserInfo,
    removeUserId,
    sendEvent,
    sendPageview,
    sendPageleavePosthog,
    sendPosthogEvent,
    sendPixelEvent,

    sendConversionEvent,
  };
};

export default analyticsService;
